'use client';

import BackButton from '@/features/basket/components/layout/back-button';
import type { Navigation as NavigationT } from '@/features/navigation/types/navigation';
import { usePathname } from '@/navigation';
import { ClientNavigation } from '@/navigation/components/navigation-extended/client-navigation';
import { NavigationSimplified } from '@/navigation/components/navigation-simplified/navigation-simplified';
import {
  ACCOUNT_ROUTES,
  BASKET_ROUTES,
  LOGIN_ROUTES,
  RESERVATION_ROUTES,
} from '@/routes';

const simplifiedPaths = [
  BASKET_ROUTES.LOGIN,
  BASKET_ROUTES.LOGIN_EMAIL,
  BASKET_ROUTES.LOGIN_GUEST,
  BASKET_ROUTES.CHECKOUT,
  BASKET_ROUTES.REGISTER,
  BASKET_ROUTES.RESET_PASSWORD,
  BASKET_ROUTES.CHANGE_PAYMENT_METHOD,
  LOGIN_ROUTES.LOGIN_EMAIL,
  LOGIN_ROUTES.REGISTER,
  LOGIN_ROUTES.VERIFY_EMAIL,
  LOGIN_ROUTES.RESET_PASSWORD,
  LOGIN_ROUTES.FORGOT_PASSWORD,
  LOGIN_ROUTES.CHANGE_PASSWORD,
  LOGIN_ROUTES.NEW_PASSWORD,
  ACCOUNT_ROUTES.ADD_REVIEW,
  ACCOUNT_ROUTES.ADD_DETAILED_REVIEW,
  RESERVATION_ROUTES.RESERVATION_UPGRADE_BASKET,
] as string[];

const backButtonMap = {
  '/basket/login': '/basket',
  '/basket/login/email': '/basket/login',
  '/basket/login/guest': '/basket/login',
  '/basket/reset-password': '/basket/login/email',
  '/basket': '<', // Go back in history
};

interface Props {
  menu: NavigationT[];
}

export const Navigation = ({ menu }: Props) => {
  const pathname = usePathname();
  const matchedBackButtonAction = Object.entries(backButtonMap)
    .find((pathname) => pathname)
    ?.at(1);

  const hideBackButton =
    pathname === BASKET_ROUTES.THANK_YOU_PAGE ||
    pathname.startsWith(ACCOUNT_ROUTES.ADD_REVIEW);

  const isHistory = matchedBackButtonAction === BASKET_ROUTES.MAIN;
  const backButtonNode = () => {
    if (hideBackButton) return null;
    if (isHistory) return <BackButton />;

    return <BackButton href={matchedBackButtonAction} />;
  };
  const isSimplifiedPath = (pathname: string) => {
    if (
      pathname.startsWith(ACCOUNT_ROUTES.ADD_REVIEW) ||
      (pathname.startsWith(ACCOUNT_ROUTES.ADD_DETAILED_REVIEW) &&
        !pathname.includes('sent'))
    ) {
      return true;
    }

    return simplifiedPaths.includes(pathname);
  };

  const isSimplified = isSimplifiedPath(pathname);

  return isSimplified ? (
    <NavigationSimplified backButton={backButtonNode()} />
  ) : (
    <ClientNavigation menu={menu} />
  );
};
